import { useEffect, useState } from 'react';
import {
  CUSTOMER_STAGES,
  REPORT_CUSTOMER_TYPES,
  REPORT_PAYMENT_METHODS,
  TRAFFIC_CHANNELS,
  hasFilters,
  type ReportFilterList,
  type ReportFilters,
} from '@ecom/contracts';
import { AsyncSelect, type AsyncOption, type OptionPage } from '@/components/async-select';
import { STAGE_LABELS } from '@/components/customer-stage';
import { useUiStore } from '@/i18n';
import type { MessageKey } from '@/i18n/messages';
import { ORDER_STATUS_LABELS } from '@/i18n/messages';
import { useT } from '@/i18n/use-t';
import { brandOptions, categoryOptions, customerGroupOptions, productOptions, reportValueOptions, staffOptions } from '@/lib/options';
import { halalasToRiyals, riyalsToHalalas } from '@/lib/price-input';
import { useReportsStore } from '@/stores/reports.store';
import { Button, Checkbox, Input, Select, cx } from './ui';

/**
 * The filters every order report reads (ADR-119): chips for what is applied,
 * and one picker to add the next. Lists of values come from the server —
 * searched and paged — never from a list held here.
 */

type Kind = ReportFilterList | 'fulfilment' | 'total';

const ENUMS: Partial<Record<ReportFilterList, readonly string[]>> = {
  status: Object.keys(ORDER_STATUS_LABELS),
  payment: REPORT_PAYMENT_METHODS,
  channel: [...TRAFFIC_CHANNELS, 'unknown'],
  customer: REPORT_CUSTOMER_TYPES,
  stage: CUSTOMER_STAGES,
};

type Source = { page: (search: string, cursor?: string) => Promise<OptionPage>; one: (id: string) => Promise<AsyncOption | null> };

const SOURCES: Partial<Record<ReportFilterList, Source>> = {
  region: reportValueOptions('region'),
  city: reportValueOptions('city'),
  coupon: reportValueOptions('coupon'),
  carrier: reportValueOptions('carrier'),
  category: categoryOptions,
  brand: brandOptions,
  product: productOptions,
  group: customerGroupOptions,
  assignee: staffOptions,
};

/** Values that are ids, so their chips need a name looked up. */
const BY_ID: ReportFilterList[] = ['category', 'brand', 'product', 'group', 'assignee'];

const KINDS: Kind[] = ['status', 'payment', 'channel', 'city', 'region', 'category', 'brand', 'product', 'customer', 'stage', 'group', 'coupon', 'carrier', 'assignee', 'fulfilment', 'total'];

export function ReportFilterBar() {
  const { filters, filterLabels, setFilters, addLabels } = useReportsStore();
  const t = useT();
  const locale = useUiStore((state) => state.locale);
  const [adding, setAdding] = useState<Kind | ''>('');

  // A saved view brings ids without their names: look each one up once.
  useEffect(() => {
    for (const kind of BY_ID) {
      for (const id of filters[kind] ?? []) {
        const key = `${kind}:${id}`;
        if (filterLabels[key] || id === 'none') continue;
        void SOURCES[kind]!.one(id).then((option) => option && addLabels({ [key]: option.label }));
      }
    }
  }, [filters, filterLabels, addLabels]);

  const valueLabel = (kind: ReportFilterList, value: string): string => {
    if (kind === 'status') return ORDER_STATUS_LABELS[value]?.[locale] ?? value;
    if (kind === 'payment') return t(`orderMethod${value}` as MessageKey);
    if (kind === 'channel') return t(`channel_${value}` as MessageKey);
    if (kind === 'customer') return t(`reportCustomer_${value}` as MessageKey);
    if (kind === 'stage') return STAGE_LABELS[value as keyof typeof STAGE_LABELS] ? t(STAGE_LABELS[value as keyof typeof STAGE_LABELS]) : value;
    if (kind === 'assignee' && value === 'none') return t('reportNobody');
    return filterLabels[`${kind}:${value}`] ?? (BY_ID.includes(kind) ? '…' : value);
  };

  const add = (kind: ReportFilterList, value: string, label?: string) => {
    const current = filters[kind] ?? [];
    if (current.includes(value)) return;
    setFilters({ ...filters, [kind]: [...current, value] }, label ? { [`${kind}:${value}`]: label } : undefined);
  };
  const remove = (kind: ReportFilterList, value: string) => {
    const next = (filters[kind] ?? []).filter((entry) => entry !== value);
    const copy: ReportFilters = { ...filters, [kind]: next };
    if (next.length === 0) delete copy[kind];
    setFilters(copy);
  };

  const chips: { id: string; label: string; onRemove: () => void }[] = [];
  for (const kind of KINDS) {
    if (kind === 'fulfilment' || kind === 'total') continue;
    for (const value of filters[kind] ?? []) {
      chips.push({ id: `${kind}:${value}`, label: `${t(`reportFilter_${kind}` as MessageKey)}: ${valueLabel(kind, value)}`, onRemove: () => remove(kind, value) });
    }
  }
  if (filters.fulfilment) {
    chips.push({ id: 'fulfilment', label: t(`reportFulfilment_${filters.fulfilment}` as MessageKey), onRemove: () => setFilters({ ...filters, fulfilment: undefined }) });
  }
  if (filters.minTotal !== undefined || filters.maxTotal !== undefined) {
    const band = [filters.minTotal !== undefined ? `≥ ${halalasToRiyals(filters.minTotal)}` : '', filters.maxTotal !== undefined ? `≤ ${halalasToRiyals(filters.maxTotal)}` : ''].filter(Boolean).join(' · ');
    chips.push({ id: 'total', label: `${t('reportFilter_total')}: ${band}`, onRemove: () => setFilters({ ...filters, minTotal: undefined, maxTotal: undefined }) });
  }

  return (
    <div className="flex flex-col gap-2 rounded-card border border-line bg-surface p-3">
      <div className="flex flex-wrap items-center gap-2">
        {chips.map((chip) => (
          <span key={chip.id} className="inline-flex items-center gap-1 rounded-full bg-brand-50 py-1 ps-3 pe-1 text-xs font-medium text-brand-700">
            {chip.label}
            <button type="button" aria-label={t('remove')} onClick={chip.onRemove} className="rounded-full px-1.5 text-brand-600 hover:bg-brand-100">
              ×
            </button>
          </span>
        ))}
        <Select value={adding} onChange={(event) => setAdding(event.target.value as Kind | '')} className="w-auto min-w-40 text-xs" aria-label={t('reportAddFilter')}>
          <option value="">{t('reportAddFilter')}</option>
          {KINDS.map((kind) => (
            <option key={kind} value={kind}>
              {t(`reportFilter_${kind}` as MessageKey)}
            </option>
          ))}
        </Select>
        {hasFilters(filters) ? (
          <Button variant="ghost" size="sm" onClick={() => setFilters({})}>
            {t('reportClearFilters')}
          </Button>
        ) : (
          <span className="text-xs text-ink-faint">{t('reportFiltersHint')}</span>
        )}
      </div>

      {adding ? <Picker kind={adding} filters={filters} onAdd={add} onSet={(next) => setFilters({ ...filters, ...next })} onDone={() => setAdding('')} valueLabel={valueLabel} /> : null}
    </div>
  );
}

function Picker({
  kind,
  filters,
  onAdd,
  onSet,
  onDone,
  valueLabel,
}: {
  kind: Kind;
  filters: ReportFilters;
  onAdd: (kind: ReportFilterList, value: string, label?: string) => void;
  onSet: (patch: Partial<ReportFilters>) => void;
  onDone: () => void;
  valueLabel: (kind: ReportFilterList, value: string) => string;
}) {
  const t = useT();
  const [min, setMin] = useState(filters.minTotal !== undefined ? halalasToRiyals(filters.minTotal) : '');
  const [max, setMax] = useState(filters.maxTotal !== undefined ? halalasToRiyals(filters.maxTotal) : '');

  if (kind === 'fulfilment') {
    return (
      <div className="flex flex-wrap items-center gap-2">
        {(['delivery', 'pickup'] as const).map((value) => (
          <Button key={value} variant={filters.fulfilment === value ? 'secondary' : 'ghost'} size="sm" onClick={() => (onSet({ fulfilment: value }), onDone())}>
            {t(`reportFulfilment_${value}` as MessageKey)}
          </Button>
        ))}
      </div>
    );
  }
  if (kind === 'total') {
    return (
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          {t('reportMinTotal')}
          <Input dir="ltr" inputMode="decimal" value={min} onChange={(event) => setMin(event.target.value)} className="w-32" />
        </label>
        <label className="flex flex-col gap-1 text-xs text-ink-muted">
          {t('reportMaxTotal')}
          <Input dir="ltr" inputMode="decimal" value={max} onChange={(event) => setMax(event.target.value)} className="w-32" />
        </label>
        <Button
          size="sm"
          onClick={() => {
            onSet({ minTotal: riyalsToHalalas(min) ?? undefined, maxTotal: riyalsToHalalas(max) ?? undefined });
            onDone();
          }}
        >
          {t('reportApplyFilter')}
        </Button>
      </div>
    );
  }
  const values = ENUMS[kind];
  if (values) {
    const chosen = filters[kind] ?? [];
    return (
      <div className="flex flex-wrap gap-x-4 gap-y-2">
        {values.map((value) => (
          <label key={value} className={cx('flex items-center gap-2 text-xs', chosen.includes(value) ? 'text-ink' : 'text-ink-muted')}>
            <Checkbox
              label={valueLabel(kind, value)}
              checked={chosen.includes(value)}
              onChange={(event) => {
                const next = event.target.checked ? [...chosen, value] : chosen.filter((entry) => entry !== value);
                onSet({ [kind]: next.length > 0 ? next : undefined });
              }}
            />
            {valueLabel(kind, value)}
          </label>
        ))}
      </div>
    );
  }
  const source = SOURCES[kind]!;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="w-72">
        <AsyncSelect
          value=""
          onChange={(id, option) => {
            if (id) onAdd(kind, id, option?.label);
          }}
          fetchPage={source.page}
          placeholder={t('reportSearchValue')}
          emptyLabel={t(`reportFilter_${kind}` as MessageKey)}
        />
      </div>
      {kind === 'assignee' ? (
        <Button variant="ghost" size="sm" onClick={() => onAdd('assignee', 'none')}>
          {t('reportNobody')}
        </Button>
      ) : null}
      <Button variant="ghost" size="sm" onClick={onDone}>
        {t('done')}
      </Button>
    </div>
  );
}
